import logger from '@eventbrite/client-logger';
import { joinPath } from '@eventbrite/path-utils';
import { replace } from 'react-router-redux';
import { Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { BASE_URL, STATUS_PATH } from '../constants';
import {
    TIME_EXPIRED_STATUS,
    TIME_EXPIRED_STATUS_NO_BACK_BUTTON,
} from '../containers/status/constants';
import { getTimeRemaining, hasExistingOrder } from '../selectors/order';
import { getExpirationTimestamp } from '../selectors/timer';
import { CheckoutState } from '../state';

export enum TimerActionType {
    startBackgroundTimer = 'startBackgroundTimer',
    updateBackgroundTimer = 'updateBackgroundTimer',
}

export interface StartBackgroundTimerAction {
    type: TimerActionType.startBackgroundTimer;
    payload: {
        expirationTimestamp: number | null;
    };
}

export interface UpdateBackgroundTimerAction {
    type: TimerActionType.updateBackgroundTimer;
    payload: {
        isTimerExpired: boolean;
    };
}

const startBackgroundTimerAction = (
    remainingDurationInMilliseconds: number | null,
): StartBackgroundTimerAction => ({
    type: TimerActionType.startBackgroundTimer,
    payload: {
        expirationTimestamp:
            remainingDurationInMilliseconds !== null
                ? Date.now() + remainingDurationInMilliseconds
                : null,
    },
});

export const startBackgroundTimer =
    (): ThunkAction<void, CheckoutState, void> => (dispatch, getState) => {
        const state = getState();

        const timeRemaining = getTimeRemaining(state);

        if (timeRemaining === undefined) {
            logger.warn('Timer Actions: Order has no timeRemaining');
            return;
        }

        dispatch(startBackgroundTimerAction(timeRemaining * 1000));
    };

const updateBackgroundTimerAction = ({
    isTimerExpired,
}: {
    isTimerExpired: boolean;
}): UpdateBackgroundTimerAction => ({
    type: TimerActionType.updateBackgroundTimer,
    payload: {
        isTimerExpired,
    },
});

export const updateBackgroundTimer =
    (): ThunkAction<void, CheckoutState, void> => (dispatch, getState) => {
        const state = getState();

        const expirationTimestamp = getExpirationTimestamp(state);

        const isTimerExpired =
            expirationTimestamp !== null
                ? Date.now() >= expirationTimestamp
                : false;

        dispatch(
            updateBackgroundTimerAction({
                isTimerExpired,
            }),
        );
    };

export const navigateToTimeExpiredStatusPage =
    () => (dispatch: Dispatch<any>, getState: () => CheckoutState) => {
        const path = hasExistingOrder(getState())
            ? TIME_EXPIRED_STATUS_NO_BACK_BUTTON
            : TIME_EXPIRED_STATUS;

        dispatch(replace(joinPath(BASE_URL, STATUS_PATH, path)));
    };

export type TimerAction =
    | StartBackgroundTimerAction
    | UpdateBackgroundTimerAction;
